import type { MockClient, MockEnvironment } from '@gotti/test-utils';
import { getScopedInputToken, isFocusScopedInputInstalled, setFocusedClient } from '@gotti/test-utils';
import type { AreaServer } from '@gotti/server';
import type { PlaygroundMode, PlaygroundRuntimeSelection, PlaygroundStepContext } from '../types.js';
import { PLAYGROUND_STEP_LOG_PREFIX } from '../protocol.js';
import { unique } from '../util/arrays.js';
import { listClients } from './clients.js';

/** Every booted area, deduped, in `env.areas` key order. */
export const listAreas = (env: MockEnvironment | null | undefined): AreaServer[] =>
  unique(Object.values(env?.areas ?? {}).filter((area): area is AreaServer => Boolean(area)));

/** `env`'s client at `index` among `listClients(env)` — what `ctx.client(index)` answers; throws naming the count when there is none. */
export const clientAt = (env: MockEnvironment | null | undefined, index = 0): MockClient => {
  const clients = listClients(env);
  const client = clients[index];
  if (!client) {
    throw new Error(`[gotti][playground] client(${index}): this playground has ${clients.length} client(s).`);
  }
  return client;
};

/** Which side owns the simulation for `selection`. */
export const modeOf = (selection: Pick<PlaygroundRuntimeSelection, 'environment'>): PlaygroundMode =>
  selection.environment?.offline ? 'offline' : 'online';

/** What a step context is built from: the selection, the environment (if any) and the render target. */
export interface StepContextInputs {
  selection: Pick<PlaygroundRuntimeSelection, 'environment'>;
  environment: MockEnvironment | null;
  target: HTMLElement;
}

/**
 * How long the contexts a test — or the run's `ready`/`game` gate — hands out may move the focus: until `end()`. A
 * step abandoned past its budget keeps running after its test has ended; a late `ctx.focus` from it must not move
 * the focus of whatever runs by then (the next test, which starts with `null`, or the dashboard after the run), so
 * it throws instead.
 */
export class StepContextLifetime {
  /** The lifetime of the contexts one test's steps get: the test. */
  static forTest(): StepContextLifetime {
    return new StepContextLifetime("this step's test", 'a step still running after its test (one abandoned past its budget)');
  }

  /** The lifetime of the context the run's `ready` and `game` get: the run, so a handle `game` builds on it works in every step. */
  static forGate(): StepContextLifetime {
    return new StepContextLifetime('the run', "the ready/game gate's context (a gate abandoned past readyTimeoutMs, or a handle game() built on it)");
  }

  private ended = false;

  /** `whose` names what ended and `straggler` what may still be running, in the error. */
  private constructor(private readonly whose: string, private readonly straggler: string) {}

  end(): void {
    this.ended = true;
  }

  /** Throws, naming `call`, once this lifetime has ended. */
  assertLive(call: string): void {
    if (this.ended) {
      throw new Error(`[gotti][playground] ${call}: ${this.whose} has ended — ${this.straggler} cannot move the focus.`);
    }
  }
}

/**
 * Builds the context handed to steps. `clients`/`areas` are live getters, so a client created or
 * removed mid-test is reflected; `env` is the environment as booted, and reading it throws when the
 * playground booted none. `game` is what the definition's `game` builder returned (`undefined` when
 * it declares none, or when this context is the gate `ready`/`game` themselves run with). `focus` works
 * until `lifetime` ends.
 */
export const createStepContext = <ClientApi, TGame = unknown>(
  inputs: StepContextInputs,
  api: ClientApi,
  game: TGame,
  lifetime: StepContextLifetime
): PlaygroundStepContext<ClientApi, TGame> => {
  const { environment } = inputs;
  return {
    api,
    game,
    get env() {
      if (!environment) {
        throw new Error(
          '[gotti][playground] ctx.env: this playground booted no environment — declare `environment` on the definition, or assign `ctx.environment` in `setup`.'
        );
      }
      return environment;
    },
    get clients() {
      return listClients(environment);
    },
    client(index = 0) {
      return clientAt(environment, index);
    },
    get areas() {
      return listAreas(environment);
    },
    focus(client) {
      lifetime.assertLive(`focus(${client})`);
      if (client === null) {
        setFocusedClient(null);
        return;
      }
      const clients = listClients(environment);
      const focused = clients[client];
      if (!focused) {
        throw new Error(`[gotti][playground] focus(${client}): this playground has ${clients.length} client(s).`);
      }
      // Every refusal below names why no key could reach the client alone: a focus that routes nothing must
      // never look like one that worked.
      if (!isFocusScopedInputInstalled()) {
        throw new Error(`[gotti][playground] focus(${client}): the focus-scoped input router is not installed on this page's window, so no key could be routed to one client.`);
      }
      // The token the process was actually scoped under — read back, never recomputed from the index.
      const token = getScopedInputToken(focused.process);
      if (token === undefined) {
        throw new Error(
          `[gotti][playground] focus(${client}): client ${client}'s keyboard input is not scoped to it — it was not created by test-utils while the playground's creation hook was registered (a client built some other way, or an outdated @gotti/test-utils), so no key can reach it alone.`
        );
      }
      setFocusedClient(token);
    },
    mode: modeOf(inputs.selection),
    target: inputs.target,
    log: (...args: unknown[]) => {
      console.info(PLAYGROUND_STEP_LOG_PREFIX, ...args);
    },
  };
};
